import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Typography, Rating, Button, Dialog, IconButton, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import GoogleMapReact from 'google-map-react';
import Viewer from 'react-images-viewer';

import useApi from 'hooks/useApi';
import videoCall from 'assets/images/icons/video-call.png';
import photoGallery from 'assets/images/icons/photo-gallery.png';
import videoCamera from 'assets/images/icons/video-camera.png';
import video from 'assets/images/icons/video.png';
import sign from 'assets/images/icons/sign.png';
import word from 'assets/images/icons/word.png';
import AvatarGroup from '@mui/material/AvatarGroup';
import Avatar from '@mui/material/Avatar';
import { formatDateValue } from 'utils/dateFormat';
import { keyframes } from '@mui/system';

// Pulse animation for the marker
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.5;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const Marker = ({ lat, lng }: { lat: number; lng: number }) => (
  <Box
    sx={{
      position: 'relative',
      transform: 'translate(-50%, -100%)',
      cursor: 'pointer',
    }}
  >
    {/* Pulse effect */}
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: 'rgba(25, 118, 210, 0.3)',
        animation: `${pulse} 2s ease-out infinite`,
      }}
    />
    {/* Main marker pin */}
    <Box
      sx={{
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))',
      }}
    >
      {/* Pin shape */}
      <Box
        sx={{
          width: 32,
          height: 32,
          backgroundColor: '#1976d2',
          borderRadius: '50% 50% 50% 0',
          transform: 'rotate(-45deg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid #fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {/* Inner dot */}
        <Box
          sx={{
            width: 10,
            height: 10,
            backgroundColor: '#fff',
            borderRadius: '50%',
            transform: 'rotate(45deg)',
          }}
        />
      </Box>
    </Box>
  </Box>
);

const TransactionDetails = () => {
  const navigate = useNavigate();
  const { historyUid } = useParams();
  const { getTransactionDetails } = useApi();

  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [coordinates, setCoordinates] = useState<any>(null);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const loadDetails = async (transId: any) => {
    const response = await getTransactionDetails(transId);
    setCoordinates({
      lat: response?.data?.location?.coordinates[0],
      lng: response?.data?.location?.coordinates[1]
    });
    setTransactionDetails(response.data);
  };

  const images =
    transactionDetails?.files?.map((img: any) => ({
      src: img.url
    })) || [];

  useEffect(() => {
    loadDetails(historyUid);
  }, []);

  const openViewer = (index: number) => {
    setCurrentImage(index);
    setViewerIsOpen(true);
  };

  const closeViewer = () => {
    setViewerIsOpen(false);
  };

  const gotoNext = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const gotoPrev = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <>
      <Box>
        <ArrowBackIcon onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} />
        <Box display="flex" alignItems="center" justifyContent="space-between" margin={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <img
              src={
                transactionDetails?.eventType === 'IMAGE'
                  ? photoGallery
                  : transactionDetails?.eventType === 'VIDEO'
                  ? videoCamera
                  : transactionDetails?.eventType === 'SESSION'
                  ? videoCall
                  : transactionDetails?.eventType === 'ESIGN'
                  ? sign
                  : transactionDetails?.eventType === 'DOCUMENT'
                  ? word
                  : word
              }
              alt="Video Call"
              width={40}
              height={40}
            />
            <Typography variant="h2" fontWeight="bold" fontFamily="Inter" textTransform="uppercase">
              {' '}
              {transactionDetails?.key}{' '}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography>
              {' '}
              {transactionDetails?.updatedAt ? formatDateValue(transactionDetails.updatedAt, true) : ''}{' '}
            </Typography>
            <Button variant="outlined" color="success">
              {' '}
              {transactionDetails?.eventStatus}{' '}
            </Button>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={4} mt={4}>
          <Box>
            <Typography fontWeight="bold">Client</Typography>
            <Typography>{transactionDetails?.team?.client}</Typography>
          </Box>
          <Box>
            <Typography fontWeight="bold">Team</Typography>
            <Typography>{transactionDetails?.team?.name}</Typography>
          </Box>
          <Box>
            <Typography fontWeight="bold">Step Information</Typography>
            <Typography>{transactionDetails?.step?.title}</Typography>
          </Box>
          <Box>
            <Typography fontWeight="bold">User</Typography>
            <Typography>
              {' '}
              {transactionDetails?.user?.firstName} {transactionDetails?.user?.lastName}{' '}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={4} mt={4}>
          <Box>
            <Typography fontWeight="bold">Location</Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Box style={{ height: '90px', width: '90px' }}>
                {coordinates ? (
                  <GoogleMapReact
                    bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY! }}
                    defaultCenter={coordinates}
                    defaultZoom={11}
                  >
                    <Marker lat={coordinates?.lat} lng={coordinates?.lng} />
                  </GoogleMapReact>
                ) : (
                  ''
                )}
              </Box>
              <Typography sx={{ wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: '300px' }}>
                {' '}
                {transactionDetails?.location?.location}{' '}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography fontWeight="bold">Attachments</Typography>
            <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
              {transactionDetails?.files?.map((img: any, index: number) => {
                const ext = img.file_name?.split('.').pop() || '';
                return (
                  <Box key={img._id} width={50} height={50} borderRadius={0} overflow="hidden" style={{ cursor: 'pointer' }}>
                    <img
                      src={ext == 'mp4' ? video : img.url}
                      alt={img.file_name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onClick={() => {
                        if (ext === 'mp4') {
                          setVideoUrl(img.url);
                          setShowVideo(true);
                        } else {
                          openViewer(index);
                        }
                      }}
                    />
                    {showVideo && (
                      <Dialog
                        open={showVideo}
                        onClose={() => setShowVideo(false)}
                        maxWidth="xl"
                        PaperProps={{
                          sx: {
                            width: 'fit-content',
                            maxWidth: '100%',
                            m: 2
                          }
                        }}
                      >
                        <Box position="relative">
                          <IconButton
                            onClick={() => setShowVideo(false)}
                            sx={{ position: 'absolute', top: 8, right: 8, color: 'red', zIndex: 1 }}
                          >
                            <HighlightOffIcon fontSize="large" />
                          </IconButton>
                          <video
                            src={videoUrl}
                            controls
                            autoPlay
                            crossOrigin="anonymous"
                            playsInline
                            style={{ width: 'auto', maxWidth: '90vw', maxHeight: '80vh', height: 'auto', display: 'block' }}
                          />
                        </Box>
                      </Dialog>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box>
            <Typography fontWeight="bold">Rating</Typography>
            <Rating name="read-only" sx={{ marginTop: '10px' }} value={transactionDetails?.rating} readOnly size="large" />
          </Box>
        </Box>

        <hr />
        <Typography variant="h4" fontWeight="bold">
          {' '}
          Session Details{' '}
        </Typography>

        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={4} mt={4}>
          <Box>
            <Typography fontWeight="bold">Session ID</Typography>
            <Typography>{transactionDetails?.team?.client}</Typography>
          </Box>
          <Box>
            <Typography fontWeight="bold">Channel Name</Typography>
            <Typography>{transactionDetails?.team?.name}</Typography>
          </Box>
          <Box>
            <Typography fontWeight="bold">Initiated By</Typography>
            <Typography>{transactionDetails?.step?.title}</Typography>
          </Box>
          <Box>
            <Typography fontWeight="bold">Answered By</Typography>
            <Typography>
              {' '}
              {transactionDetails?.user?.firstName} {transactionDetails?.user?.lastName}{' '}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" height={300} marginTop={4}>
          <Box flex={1} display="flex" flexDirection="column" alignItems="start" justifyContent="start">
            <Typography fontWeight="bold">Users</Typography>
            <AvatarGroup max={4}>
              <Avatar alt="Remy Sharp" src="/static/images/avatar/1.jpg" />
              <Avatar alt="Travis Howard" src="/static/images/avatar/2.jpg" />
              <Avatar alt="Cindy Baker" src="/static/images/avatar/3.jpg" />
              <Avatar alt="Agnes Walker" src="/static/images/avatar/4.jpg" />
              <Avatar alt="Trevor Henderson" src="/static/images/avatar/5.jpg" />
            </AvatarGroup>

            <Typography fontWeight="bold">Screenshots</Typography>

            <Typography fontWeight="bold">Recordings</Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box flex={1} display="flex" flexDirection="column" alignItems="start" justifyContent="start" marginLeft={2}>
            <Typography fontWeight="bold">Chat History</Typography>
            <Typography>No chats yet</Typography>
          </Box>
        </Box>

        {/* <Typography> <strong>Source: </strong> {transactionDetails?.eventSource} </Typography>
            <Typography> <strong>Type: </strong> {transactionDetails?.eventType} </Typography>
            <Typography> <strong>Team: </strong> {transactionDetails?.team?.name} </Typography>

            <Typography> <strong>Key: </strong> {transactionDetails?.key} </Typography>
            <Typography> <strong>Rating: </strong> <Rating name="read-only" value={transactionDetails?.rating} readOnly /> </Typography>
            <Typography> <strong>Step Title: </strong> {transactionDetails?.step?.title} </Typography>
            <Typography> <strong>Step Type: </strong> {transactionDetails?.step?.stepType} </Typography>
            <Typography> <strong>Step Status: </strong> {transactionDetails?.stepStatus} </Typography>

            <Typography> <strong>Location: </strong> {transactionDetails?.location?.location} </Typography>
            <Typography> <strong>User: </strong> {transactionDetails?.user?.firstName} {transactionDetails?.user?.lastName} </Typography>
               */}
      </Box>

      <Viewer
        imgs={images}
        currImg={currentImage}
        isOpen={viewerIsOpen}
        onClickPrev={gotoPrev}
        onClickNext={gotoNext}
        onClose={closeViewer}
        showCloseBtn
        showImgCount
        backdropCloseable
      />
    </>
  );
};
export default TransactionDetails;
